import { z } from 'zod';

/** 4탭 분류 (PENDING=신청, CONFIRMED=진행, COMPLETED=완료, NOT_SELECTED=미선정) */
export const SeedingRecruitmentStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'COMPLETED',
  'NOT_SELECTED',
]);
export type SeedingRecruitmentStatus = z.infer<typeof SeedingRecruitmentStatusSchema>;

/** raw 참여 상태 (4탭 분류와 별도) */
export const SeedingParticipationStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'REJECTED',
  'AUTO_TIMEOUT',
  'NOT_SELECTED',
  'SELF_WITHDRAWN',
  'EXPIRED',
  'COMPLETED',
]);
export type SeedingParticipationStatus = z.infer<typeof SeedingParticipationStatusSchema>;

export const SeedingContentChannelSchema = z.enum([
  'INSTAGRAM',
  'YOUTUBE',
  'TWITTER',
  'TIKTOK',
  'NAVER_BLOG',
  'THREADS',
  'SNAP',
]);
export type SeedingContentChannel = z.infer<typeof SeedingContentChannelSchema>;

export const SeedingFulfillmentStatusSchema = z.enum([
  'SHIPPING_READY',
  'SHIPPING',
  'DELIVERED',
  'CANCELLED',
]);
export type SeedingFulfillmentStatus = z.infer<typeof SeedingFulfillmentStatusSchema>;

/** 콘텐츠 유형 */
export const SeedingContentTypeSchema = z.enum([
  'IMAGE',
  'SHORT_FORM',
  'LONG_FORM',
  'TEXT',
  'BLOG_POST',
]);
export type SeedingContentType = z.infer<typeof SeedingContentTypeSchema>;

/** 등록 콘텐츠 이력 — COMPLETED 탭에서만 채워짐 */
export const RegisteredContentSchema = z
  .object({
    channel: SeedingContentChannelSchema,
    contentType: SeedingContentTypeSchema,
    registeredAt: z.string(),
  })
  .describe('RegisteredContentSchema');
export type RegisteredContent = z.infer<typeof RegisteredContentSchema>;

/** 시딩 상품 슬롯 1건 (대표 = 첫 번째 goods) */
const CampaignGoodsItemSchema = z
  .object({
    goodsNo: z.number(),
    goodsName: z.string(),
    imageUrl: z.string(),
    price: z.number(),
    /** 최종 금액 (세일+쿠폰+추가할인, 무신사 전용) */
    finalPrice: z.number().nullable().optional(),
  })
  .describe('CampaignGoodsItemSchema');
export type CampaignGoodsItem = z.infer<typeof CampaignGoodsItemSchema>;

export const CampaignGoodsSlotSchema = z
  .object({
    slotNo: z.number(),
    goods: z.array(CampaignGoodsItemSchema),
  })
  .describe('CampaignGoodsSlotSchema');
export type CampaignGoodsSlot = z.infer<typeof CampaignGoodsSlotSchema>;

export const GetSeedingMeRecruitmentServiceRequestSchema = z
  .object({
    searchStatus: SeedingRecruitmentStatusSchema,
    page: z.number().min(1).optional(),
    size: z.number().min(1).max(100).optional(),
  })
  .describe('GetSeedingMeRecruitmentServiceRequestSchema');

export type GetSeedingMeRecruitmentServiceRequest = z.infer<
  typeof GetSeedingMeRecruitmentServiceRequestSchema
>;

const SeedingRecruitmentItemSchema = z
  .object({
    participationId: z.number(),
    campaignId: z.number(),
    searchStatus: SeedingRecruitmentStatusSchema,
    participationStatus: SeedingParticipationStatusSchema,
    goodsImage: z.string(),
    brandName: z.string(),
    // 이벤트 로그 brand_id / campaign_title 용 (BE 0625 응답 제공).
    brandId: z.string(),
    title: z.string(),
    goodsName: z.string(),
    goodsPrice: z.number(),
    snsChannels: z.array(SeedingContentChannelSchema),
    timerEndsAt: z.string().nullable(),
    fulfillmentStatus: SeedingFulfillmentStatusSchema.nullable(),
    announcementAt: z.string().nullable(),
    messageToBrand: z.string().nullable(),
    createdAt: z.string(),
    /** 콘텐츠 등록 이력 — COMPLETED 만 채움, 그 외 빈 배열 */
    registeredContents: z.array(RegisteredContentSchema),
    /** 시딩 상품 슬롯 — "외 N개" = length - 1 */
    goodsSlots: z.array(CampaignGoodsSlotSchema),
  })
  .describe('SeedingRecruitmentItemSchema');

export type SeedingRecruitmentItem = z.infer<typeof SeedingRecruitmentItemSchema>;

export const GetSeedingMeRecruitmentServiceResponseSchema = z
  .object({
    list: z.array(SeedingRecruitmentItemSchema),
    pagination: z.object({
      hasNext: z.boolean(),
      nextPage: z.number().nullable(),
      total: z.number(),
    }),
  })
  .describe('GetSeedingMeRecruitmentServiceResponseSchema');

export type GetSeedingMeRecruitmentServiceResponse = z.infer<
  typeof GetSeedingMeRecruitmentServiceResponseSchema
>;
